/**
 * @file Einen Beleg in einem neuen Tab oeffnen, ohne die Anwendung zu verlassen.
 *
 * WARUM ES DIESE DATEI GIBT
 * -------------------------
 * Bis zum 17.09.2026 wurde der Beleg ueber ein erzeugtes <a href download>
 * geoeffnet. Bei einer Adresse FREMDER HERKUNFT - und die signierte S3-Adresse
 * ist immer fremd - ignorieren Chrome, Firefox und Safari das
 * download-Attribut und NAVIGIEREN stattdessen. Die Einzelseitenanwendung war
 * damit weg. Kevin am 17.09.2026: "die pdf rechnung soll sich im neuen tab
 * oeffnen und nicht aus der software raus gehen."
 *
 * DIE HARTE STELLE
 * ----------------
 * Die Adresse liegt beim Klick noch nicht vor - sie wird erst geholt, und seit
 * der Beleg bei Bedarf erzeugt wird, dauert das im Erstfall 5-7 SEKUNDEN.
 * `window.open` NACH einem await ist aber keine Nutzeraktivierung mehr und
 * wird vom Popup-Blocker abgewiesen. Deshalb wird der Tab SYNCHRON im Klick
 * geoeffnet und erst danach umgeleitet.
 *
 * Und deshalb steht in dem Tab eine Warteseite: Ohne sie sieht der Nutzer
 * mehrere Sekunden eine weisse Flaeche mit dem Titel "about:blank" - das ist
 * der Grund, warum der vorab geoeffnete Tab einen schlechten Ruf hat. Mit
 * Rechnungsnummer im Titel ist es eine erklaerte Wartezeit statt eines Defekts.
 *
 * DIESE DATEI IST EINE GEMEINSAME FASSUNG. Sie liegt wortgleich in allen vier
 * Oberflaechen (tax, tax2, pro, plan), weil die Mechanik nicht auseinander-
 * laufen darf. Wer sie hier aendert, aendert sie dort mit.
 */

/** Ein vorbereiteter Tab, der auf seine Adresse wartet. */
export interface PendingTab {
  /** true, wenn wirklich ein Tab geoeffnet wurde. */
  readonly opened: boolean;
  /** Den Beleg im Tab anzeigen. Gibt false zurueck, wenn das nicht ging. */
  show(url: string): boolean;
  /** Den Tab wieder schliessen - nur vor `show` zuverlaessig moeglich. */
  cancel(): void;
}

/**
 * Laeuft die Anwendung in Electron?
 *
 * DORT GIBT ES KEINE TABS. Der Hauptprozess faengt `new-window` ab und schickt
 * die Adresse an `shell.openExternal` - `window.open` liefert deshalb immer
 * null. Schlimmer: Ein vorab geoeffnetes about:blank wuerde dort einen LEEREN
 * Systembrowser aufreissen. In Electron wird deshalb gar nicht erst vorab
 * geoeffnet, sondern nach dem Abruf einmal `window.open(url)` gerufen - das
 * landet als `shell.openExternal(url)` beim Betrachter des Systems, und die
 * Anwendung selbst bleibt unberuehrt. Genau das ist dort "nicht rausgehen".
 *
 * In DIESEM Repo ist das der Normalfall, nicht die Ausnahme: `electron/src/
 * main.ts` haengt `handleRedirect` an `new-window` UND `will-navigate` und
 * ruft `shell.openExternal(url)`. Die Erkennung traegt hier, weil der
 * Hauptprozess den User-Agent nicht ueberschreibt - Electron 16 stellt
 * "Electron/16.x" hinein. Es ist dieselbe Regel, nach der das Paket
 * `is-electron` in `src/_lib/hooks/useLogin.ts` entscheidet.
 */
export function isElectron(): boolean {
  if (typeof navigator === "undefined") return false;
  return /electron/i.test(navigator.userAgent);
}

/** Die Warteseite. Bewusst ohne externe Mittel - der Tab laedt nichts nach. */
function waitingPage(title: string): string {
  const safeTitle = title.replace(/[&<>"]/g, (c) =>
    c === "&" ? "&amp;" : c === "<" ? "&lt;" : c === ">" ? "&gt;" : "&quot;"
  );

  return `<!doctype html><html lang="de"><head><meta charset="utf-8">
<title>${safeTitle}</title>
<style>
  :root { color-scheme: light dark; }
  body { margin:0; min-height:100vh; display:flex; align-items:center;
         justify-content:center; font:16px/1.5 system-ui,-apple-system,sans-serif;
         background:#fff; color:#1a1a1a; }
  @media (prefers-color-scheme: dark) { body { background:#1a1a1a; color:#f0f0f0; } }
  .box { text-align:center; padding:2rem; }
  .ring { width:36px; height:36px; margin:0 auto 1.25rem; border-radius:50%;
          border:3px solid currentColor; border-top-color:transparent;
          animation:spin 1s linear infinite; opacity:.55; }
  @keyframes spin { to { transform:rotate(360deg); } }
  @media (prefers-reduced-motion:reduce) { .ring { animation:none; } }
  p { margin:0; }
</style></head><body>
<div class="box" role="status" aria-live="polite">
  <div class="ring"></div>
  <p>Ihr Beleg wird erzeugt.<br>Dieser Tab öffnet ihn von selbst.</p>
</div></body></html>`;
}

/**
 * Einen Tab oeffnen, der auf seinen Beleg wartet.
 *
 * MUSS SYNCHRON IM KLICK-BEHANDLER GERUFEN WERDEN - vor jedem await. Danach
 * ist die Nutzeraktivierung verbraucht und der Blocker weist ab.
 *
 * @param title - Tab-Titel, z.B. "Rechnung ABCD1234-0001". Er ist fuer Nutzer
 *   mit Vorleseprogramm die einzige verlaessliche Auskunft ueber die Wartezeit,
 *   deshalb gehoert die Rechnungsnummer hinein.
 *
 * @returns Der vorbereitete Tab. `opened === false` heisst: Der Aufrufer muss
 *   den Rueckfall gehen (Electron, oder ein harter Blocker).
 */
export function openPendingTab(title: string): PendingTab {
  if (typeof window === "undefined" || isElectron()) {
    return { opened: false, show: (): boolean => false, cancel: (): void => undefined };
  }

  let tab: Window | null = null;
  try {
    tab = window.open("", "_blank");
  } catch {
    tab = null;
  }

  if (tab === null) {
    return { opened: false, show: (): boolean => false, cancel: (): void => undefined };
  }

  try {
    tab.document.open();
    tab.document.write(waitingPage(title));
    tab.document.close();
    // VOR der Umleitung. Danach ist der Tab fremder Herkunft und der
    // Setzer unerreichbar. `window.open(..., "noopener")` waere sauberer,
    // liefert aber null - Griff und noopener gibt es nicht zusammen.
    tab.opener = null;
  } catch {
    // Eine strenge CSP kann das Schreiben verhindern. Der Tab ist dann
    // leer, aber brauchbar - das ist kein Grund, den Weg aufzugeben.
  }

  return {
    opened: true,
    show(url: string): boolean {
      // Der Nutzer kann den Wartetab laengst geschlossen haben. Dann ist
      // nichts mehr zu tun - und nichts mehr aufzuraeumen.
      if (tab === null || tab.closed) return false;

      try {
        // replace, nicht href: Die Warteseite darf nicht im Verlauf
        // stehen, sonst landet "Zurueck" wieder im Wartezustand.
        tab.location.replace(url);
        return true;
      } catch {
        // DIE UMLEITUNG IST GESCHEITERT, DER TAB STEHT ABER NOCH.
        //
        // Das ist die gefaehrlichere der beiden Lagen, in denen diese
        // Funktion false meldet: Der Tab ist NICHT geschlossen, er zeigt
        // weiter die Warteseite und dreht seinen Ring - fuer immer, denn
        // umgeleitet wird er nie mehr.
        //
        // Aufgeraeumt wird deshalb HIER und nicht beim Aufrufer. Vier
        // Aufrufer koennen die beiden Lagen am blossen `false` gar nicht
        // auseinanderhalten, und drei von vier haben genau deshalb den
        // Wartetab stehen lassen. Wer den Tab geoeffnet hat, raeumt ihn
        // auch ab.
        try {
          tab.close();
        } catch {
          // Dann bleibt er stehen - mehr koennen wir nicht tun.
        }
        return false;
      }
    },
    cancel(): void {
      if (tab === null || tab.closed) return;
      try {
        tab.close();
      } catch {
        // Nach einer Umleitung nicht mehr zuverlaessig moeglich.
      }
    },
  };
}

/**
 * Der Rueckfall: ohne vorbereiteten Tab oeffnen.
 *
 * Greift in Electron (wo er ueber shell.openExternal beim Systembetrachter
 * landet) und bei einem harten Popup-Blocker. Im zweiten Fall kann auch das
 * abgewiesen werden - dann bleibt die Anwendung stehen und der Aufrufer meldet
 * den Fehlschlag, statt still nichts zu tun.
 */
export function openDirectly(url: string): boolean {
  if (typeof window === "undefined") return false;

  // KEIN "noopener" IN DEN MERKMALEN, und das ist Absicht.
  //
  // Mit noopener - und mit noreferrer, das es einschliesst - liefert
  // window.open laut Spezifikation IMMER null, AUCH WENN DER TAB AUFGING.
  // Der Rueckgabewert waere als Erfolgssignal damit wertlos: Der Aufrufer
  // meldete auf dem Erfolgspfad einen Fehlschlag und zeigte dem Nutzer eine
  // Fehlermeldung neben seinem gerade geoeffneten Beleg.
  //
  // Den Schutz gibt es trotzdem: Der Griff wird gleich danach genullt, noch
  // bevor die Zielseite geladen ist - derselbe Weg wie beim Wartetab.
  try {
    const tab = window.open(url, "_blank");

    if (tab === null) {
      // In Electron faengt der Hauptprozess "new-window" ab und schickt
      // die Adresse an shell.openExternal. null ist dort der NORMALFALL
      // und bedeutet Erfolg, nicht Blockade.
      return isElectron();
    }

    try {
      tab.opener = null;
    } catch {
      // Nach einer sofortigen Umleitung nicht mehr erreichbar. Der Tab
      // ist trotzdem offen - das Ergebnis bleibt Erfolg.
    }

    return true;
  } catch {
    return false;
  }
}
